import { Field } from '@lucidtech/las-sdk-browser';
import { memo } from 'react';
import { ButtonProps } from 'react-aria-components';

import { merge } from '@/components';
import { Badge, Button, Flex, Text } from '@/components/core';
import { ErrorDuotoneIcon, TableFieldIcon, WarningDuotoneIcon } from '@/components/icons';
import { selectFromFieldConfig, useDocumentStore, useValidationStore } from '@/store';
import { getErrors, getWarnings, toString } from '@/utils';

import { FieldLabel } from './FieldLabel';

type TableRefFieldProps = ButtonProps & {
  name: string;
  field: Field;
};

const ITableRefField = ({ name, field, className, ...rest }: TableRefFieldProps) => {
  const config = useDocumentStore(selectFromFieldConfig(name));
  const FieldComponent = useDocumentStore((state) => state.form.Field);

  const setCurrentTable = useValidationStore((state) => state.setCurrentTable);
  const currentTable = useValidationStore((state) => state.currentTable);
  const hideTableView = useValidationStore((state) => state.hideTableView);
  const tableViewIsHidden = useValidationStore((state) => state.tableViewIsHidden);

  return (
    <FieldComponent
      name={name}
      key={name}
      children={(formField) => {
        const tablefs = Object.keys(formField.form.fieldInfo).filter((key) => key.startsWith(name));
        const numRows = formField.getValue()?.length ?? 0;

        let fieldsWithErrors = 0;
        let fieldsWithWarnings = 0;
        for (const tablef of tablefs) {
          const meta = formField.form.getFieldMeta(tablef);
          if (getErrors(meta).length > 0) ++fieldsWithErrors;
          if (getWarnings(meta).length > 0 && !meta.isReviewed) ++fieldsWithWarnings;
        }

        return (
          <div className="flex flex-col gap-1">
            <FieldLabel name={config?.name || name} description={toString(config?.description)} hideLabel={false} />

            <Button
              {...rest}
              variant="plain"
              className={merge(
                className,
                'focus:bg-primary-25 focus:border-primary-200 w-full justify-start border border-gray-200 shadow-xs'
              )}
              onFocus={() => {
                if (currentTable?.field !== field) setCurrentTable(name, field);
                if (tableViewIsHidden) {
                  window.requestAnimationFrame(() => {
                    hideTableView(false);
                  });
                }
              }}
            >
              <TableFieldIcon />
              <div className="flex grow items-center gap-2 text-start">
                <Text>Rows</Text>
                <div className="flex h-6 w-6 items-center justify-center rounded-full border-1 border-gray-200 bg-gray-50 text-sm text-gray-600">
                  <Text>{numRows}</Text>
                </div>
              </div>
              {fieldsWithErrors > 0 ? (
                <Badge variant="error">
                  <ErrorDuotoneIcon />
                  <Text>{fieldsWithErrors} fields have errors</Text>
                </Badge>
              ) : fieldsWithWarnings > 0 ? (
                <Badge variant="warning">
                  <WarningDuotoneIcon />
                  <Text>{fieldsWithWarnings} fields have warnings</Text>
                </Badge>
              ) : null}
            </Button>
          </div>
        );
      }}
    />
  );
};

export const TableRefField = memo(ITableRefField) as typeof ITableRefField;
